import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/Input";
import { AtSign, Clock, Loader2, Phone, Search, User } from "lucide-react";
import { fetchLeads } from "../../services/leadService.js";
import { Badge } from "../ui/Badge.jsx";
import VoiceUpdateButton from "../VoiceUpdateButton.jsx";

const LeadSearch = ({ setSearchQuery, searchQuery }) => {
  const navigate = useNavigate();
  const isAtSearch = searchQuery.startsWith("@");
  const atQuery = isAtSearch ? searchQuery.slice(1) : "";

  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click — but not if a modal/overlay is open (z-index >= 100)
  useEffect(() => {
    const handleClick = (e) => {
      // Don't close if click is inside a modal overlay (voice review etc.)
      const target = e.target;
      if (target.closest("[class*='z-[100]']") || target.closest("[class*='z-[101]']")) return;

      if (dropdownRef.current && !dropdownRef.current.contains(target) &&
          inputRef.current && !inputRef.current.contains(target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Fetch suggestions when @ is typed
  useEffect(() => {
    if (!isAtSearch) {
      setShowDropdown(false);
      return;
    }

    setShowDropdown(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetchLeads({
          page: 1,
          limit: 8,
          search: atQuery,
          status: "",
          deal_status: "",
        });
        setSuggestions(res?.leads || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, isAtSearch, atQuery]);

  const handleSelect = (lead) => {
    setShowDropdown(false);
    setSearchQuery("");
    navigate(`/lead/${lead.id}`);
  };

  const handleChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  };

  return (
    <div className="relative" ref={inputRef}>
      {isAtSearch ? (
        <AtSign className="absolute w-4 h-4 left-4 top-[50%] translate-y-[-50%] text-[#00a8b4]" />
      ) : (
        <Search className="absolute w-4 h-4 left-4 top-[50%] translate-y-[-50%] text-muted-foreground" />
      )}
      <Input
        type="text"
        placeholder="Search leads or type @ for quick find..."
        value={searchQuery}
        onChange={handleChange}
        className={`pl-10 text-sm border-none rounded-full h-9 ${
          isAtSearch ? "bg-[#00a8b4]/5 ring-1 ring-[#00a8b4]/30" : "bg-muted"
        }`}
      />

      {/* @ Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-11 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-[60vh] overflow-y-auto"
        >
          {/* Hint when just @ */}
          {atQuery === "" && !loading && (
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-[10px] text-gray-400 font-medium">
                Type name, phone, or email to find a lead
              </p>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={16} className="animate-spin text-gray-400" />
            </div>
          )}

          {/* Results */}
          {!loading && suggestions.length > 0 && (
            <>
              {atQuery === "" && (
                <div className="px-3 py-1.5 bg-gray-50">
                  <p className="text-[9px] text-gray-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                    <Clock size={9} /> Recent Leads
                  </p>
                </div>
              )}
              {suggestions.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                >
                  <button
                    onClick={() => handleSelect(lead)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00a8b4]/20 to-[#00c8d3]/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-[#00a8b4]">
                        {lead.name?.[0]?.toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-800 truncate capitalize">
                          {lead.name}
                        </p>
                        <Badge variant="secondary" className="text-[9px] capitalize shrink-0">
                          {String(lead.deal_status || "new").replace(/_/g, " ")}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        {lead.phone_number && (
                          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <Phone size={8} /> {lead.phone_number}
                          </span>
                        )}
                        {lead.email && (
                          <span className="text-[10px] text-gray-400 truncate max-w-[120px]">
                            {lead.email}
                          </span>
                        )}
                        {lead.created_at && (
                          <span className="text-[10px] text-gray-300 ml-auto shrink-0">
                            {formatTime(lead.created_at)}
                          </span>
                        )}
                      </div>
                      {(lead.reference_label || lead.reference) && (
                        <span className="text-[9px] text-gray-400">
                          Source: {lead.reference_label || lead.reference}
                        </span>
                      )}
                    </div>
                  </button>
                  <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                    <VoiceUpdateButton
                      leadId={lead.id}
                      leadName={lead.name}
                      onUpdated={() => {
                        // Refresh suggestions
                        setSearchQuery(searchQuery);
                      }}
                    />
                  </div>
                </div>
              ))}
            </>
          )}

          {/* No results */}
          {!loading && suggestions.length === 0 && atQuery.length > 0 && (
            <div className="flex flex-col items-center py-4 text-gray-400">
              <User size={20} className="mb-1 text-gray-300" />
              <p className="text-xs">No leads found for "{atQuery}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadSearch;
